import { useEffect, useMemo, useState } from "react";
import { Avatar, Button, Card, Checkbox, Dialog, IconButton, Input, Select, StatusPill, Tag } from "../components";
import { Icon, useLucideIcons } from "../components/Icon";
import { api } from "../lib/api";
import { useIsMobile } from "../lib/hooks";
import type { ScheduleEvent, Student } from "../lib/types";

// ── Constants ─────────────────────────────────────────────────────────────

const GRADE_OPTIONS = [
  "幼儿园",
  "一年级", "二年级", "三年级", "四年级", "五年级", "六年级",
  "初一", "初二", "初三",
  "高一", "高二", "高三",
  "大一", "大二", "大三", "大四",
  "其他",
].map(g => ({ value: g, label: g }));

const SUBJECT_OPTIONS = ["数学", "语文", "英语", "物理", "化学", "生物", "地理", "历史", "政治"]
  .map(s => ({ value: s, label: s }));

const TYPE_OPTIONS = ["一对一", "班课", "小班"].map(t => ({ value: t, label: t }));

const STATUS_OPTIONS = [
  { value: "upcoming", label: "在读" },
  { value: "taught", label: "已毕业" },
  { value: "leave", label: "请假中" },
  { value: "absent", label: "暂停" },
];

// ── StudentDialog ─────────────────────────────────────────────────────────

interface StudentDialogProps {
  open: boolean;
  editStudent?: Student;
  onClose: () => void;
  onSaved: (student: Student) => void;
  onDeleted: (id: number) => void;
}

function StudentDialog({ open, editStudent, onClose, onSaved, onDeleted }: StudentDialogProps) {
  const isEdit = !!editStudent;
  const [name, setName] = useState("");
  const [grade, setGrade] = useState("三年级");
  const [subject, setSubject] = useState("数学");
  const [type, setType] = useState("一对一");
  const [phone, setPhone] = useState("");
  const [remain, setRemain] = useState(0);
  const [rate, setRate] = useState(100);
  const [status, setStatus] = useState("upcoming");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [saving, setSaving] = useState(false);

  useLucideIcons([open]);

  useEffect(() => {
    if (!open) return;
    setConfirmDelete(false);
    setSaving(false);
    if (editStudent) {
      setName(editStudent.name);
      setGrade(editStudent.grade);
      setSubject(editStudent.subject);
      setType(editStudent.type);
      setPhone(editStudent.phone ?? "");
      setRemain(editStudent.remain);
      setRate(editStudent.rate ?? 100);
      setStatus(editStudent.status ?? "upcoming");
    } else {
      setName("");
      setGrade("三年级");
      setSubject("数学");
      setType("一对一");
      setPhone("");
      setRemain(0);
      setRate(100);
      setStatus("upcoming");
    }
  }, [open, editStudent]);

  const handleSave = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      const payload = { name: name.trim(), grade, subject, type, phone, remain, rate, status };
      if (isEdit && editStudent) {
        const updated = await api.updateStudent(editStudent.id, payload);
        onSaved(updated);
      } else {
        const created = await api.createStudent(payload);
        onSaved(created as Student);
      }
    } catch {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!editStudent) return;
    setSaving(true);
    try {
      await api.deleteStudent(editStudent.id);
      onDeleted(editStudent.id);
    } catch {
      setSaving(false);
    }
  };

  if (!open) return null;

  const footer = isEdit ? (
    <>
      {confirmDelete ? (
        <>
          <span style={{ fontSize: 13, color: "var(--danger-fg)", flex: 1 }}>确认删除该学员？</span>
          <Button variant="secondary" onClick={() => setConfirmDelete(false)}>取消</Button>
          <Button variant="danger" onClick={handleDelete} disabled={saving}>确认删除</Button>
        </>
      ) : (
        <>
          <Button variant="ghost" onClick={() => setConfirmDelete(true)} style={{ color: "var(--danger-fg)" }}>
            删除学员
          </Button>
          <div style={{ flex: 1 }} />
          <Button variant="secondary" onClick={onClose}>取消</Button>
          <Button variant="primary" onClick={handleSave} disabled={!name.trim() || saving}>
            {saving ? "保存中…" : "保存修改"}
          </Button>
        </>
      )}
    </>
  ) : (
    <>
      <Button variant="secondary" onClick={onClose}>取消</Button>
      <Button variant="primary" onClick={handleSave} disabled={!name.trim() || saving}>
        {saving ? "保存中…" : "保存"}
      </Button>
    </>
  );

  return (
    <Dialog
      open={open}
      icon={<Icon name={isEdit ? "user-pen" : "user-plus"} size={20} />}
      title={isEdit ? "编辑学员" : "新增学员"}
      onClose={onClose}
      footer={footer}
      closeOnBackdrop={false}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 14, marginTop: 8 }}>
        <Input
          label="姓名"
          value={name}
          placeholder="请输入学员姓名"
          autoFocus
          onChange={e => setName(e.target.value)}
        />
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <Select
            label="年级"
            value={grade}
            options={GRADE_OPTIONS}
            onChange={e => setGrade(e.target.value)}
          />
          <Select
            label="科目"
            value={subject}
            options={SUBJECT_OPTIONS}
            onChange={e => setSubject(e.target.value)}
          />
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <Select
            label="课型"
            value={type}
            options={TYPE_OPTIONS}
            onChange={e => setType(e.target.value)}
          />
          <Input
            label="剩余课时"
            type="number"
            value={String(remain)}
            min="0"
            step="0.5"
            onChange={e => setRemain(Math.max(0, Number(e.target.value)))}
          />
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <Input
            label="课时费/课时 (¥)"
            type="number"
            value={String(rate)}
            min="0"
            step="10"
            onChange={e => setRate(Math.max(0, Number(e.target.value)))}
          />
          <Input
            label="联系电话"
            type="tel"
            value={phone}
            placeholder="家长联系电话（选填）"
            onChange={e => setPhone(e.target.value)}
          />
        </div>
        <Select
          label="学员状态"
          value={status}
          options={STATUS_OPTIONS}
          onChange={e => setStatus(e.target.value)}
        />
      </div>
    </Dialog>
  );
}

// ── StudentCalendarDialog ──────────────────────────────────────────────────

const CN_WEEKDAY = ["日", "一", "二", "三", "四", "五", "六"];
const EV_COLOR: Record<string, string> = {
  taught: "var(--teal-500)",
  upcoming: "var(--brand)",
  leave: "var(--accent)",
  absent: "var(--danger-fg)",
};

function StudentCalendarDialog({
  open, student, events, onClose,
}: {
  open: boolean;
  student: Student | undefined;
  events: ScheduleEvent[];
  onClose: () => void;
}) {
  useLucideIcons([open]);
  if (!open || !student) return null;

  const myEvents = events
    .filter(e => e.date && (e.student_id === student.id || e.studentIds?.includes(student.id)))
    .sort((a, b) => a.date!.localeCompare(b.date!));

  const taughtHours = myEvents.filter(e => e.status === "taught").reduce((s, e) => s + (e.hours ?? 0), 0);

  const byMonth: Record<string, ScheduleEvent[]> = {};
  for (const ev of myEvents) {
    const key = ev.date!.slice(0, 7);
    (byMonth[key] ??= []).push(ev);
  }
  const months = Object.keys(byMonth).sort();

  return (
    <Dialog
      open={open}
      icon={<Icon name="calendar-days" size={20} />}
      title={`${student.name} · 课程日历`}
      onClose={onClose}
      footer={<Button variant="secondary" onClick={onClose}>关闭</Button>}
    >
      <div style={{ display: "flex", gap: 16, fontSize: 13, color: "var(--text-subtle)", marginTop: 4, flexWrap: "wrap" }}>
        <span>共 <strong style={{ color: "var(--text-strong)" }}>{myEvents.length}</strong> 节</span>
        <span>已上 <strong style={{ color: "var(--teal-500)" }}>{taughtHours % 1 === 0 ? taughtHours : taughtHours.toFixed(1)}</strong> 课时</span>
        <span>剩余 <strong style={{ color: "var(--text-strong)" }}>{student.remain % 1 === 0 ? student.remain : student.remain.toFixed(1)}</strong> 课时</span>
      </div>

      <div style={{ maxHeight: "55vh", overflowY: "auto", display: "flex", flexDirection: "column", gap: 16, marginTop: 12 }}>
        {months.length === 0 ? (
          <div style={{ color: "var(--text-subtle)", fontSize: 13, padding: "20px 0", textAlign: "center" }}>暂无课程安排。</div>
        ) : months.map(month => {
          const [y, m] = month.split("-").map(Number);
          const mEvs = byMonth[month];
          return (
            <div key={month}>
              <div style={{
                fontSize: 13, fontWeight: 700, color: "var(--text-strong)",
                marginBottom: 8, paddingBottom: 6, borderBottom: "1px solid var(--divider)",
                display: "flex", alignItems: "center", gap: 6,
              }}>
                <span>{y}年{m}月</span>
                <span style={{ fontWeight: 400, fontSize: 12, color: "var(--text-subtle)" }}>{mEvs.length} 节</span>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {mEvs.map((ev, i) => {
                  const d = new Date(ev.date! + "T00:00:00");
                  const color = EV_COLOR[ev.status] ?? "var(--brand)";
                  return (
                    <div key={ev.id ?? i} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div style={{
                        width: 40, height: 40, borderRadius: "var(--radius-md)",
                        background: color + "18", border: `1.5px solid ${color}40`,
                        display: "flex", flexDirection: "column",
                        alignItems: "center", justifyContent: "center", flexShrink: 0,
                      }}>
                        <span style={{ fontSize: 14, fontWeight: 800, lineHeight: 1, color, fontFamily: "var(--font-num)" }}>
                          {d.getDate()}
                        </span>
                        <span style={{ fontSize: 10, color, lineHeight: 1, marginTop: 1 }}>
                          周{CN_WEEKDAY[d.getDay()]}
                        </span>
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-strong)", fontFamily: "var(--font-num)" }}>
                          {ev.slot}{ev.end_time ? ` – ${ev.end_time}` : ""}
                        </div>
                        <div style={{ fontSize: 12, color: "var(--text-subtle)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {ev.subject}
                        </div>
                      </div>
                      <StatusPill status={ev.status} dot={false} />
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </Dialog>
  );
}

// ── People ─────────────────────────────────────────────────────────────────

export interface PeopleProps {
  onSchedule?: (student: Student) => void;
  searchTerm?: string;
  addOpen?: boolean;
  onAddClose?: () => void;
  onStudentSaved?: (student: Student) => void;
  onStudentDeleted?: (id: number) => void;
  events?: ScheduleEvent[];
}

function toDateStr(d: Date) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export function People({ onSchedule, searchTerm = "", addOpen = false, onAddClose, onStudentSaved, onStudentDeleted, events = [] }: PeopleProps) {
  const isMobile = useIsMobile();
  const [students, setStudents] = useState<Student[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editStudent, setEditStudent] = useState<Student | undefined>();
  const [calendarStudent, setCalendarStudent] = useState<Student | undefined>();
  const [gradeFilter, setGradeFilter] = useState("");
  const [subjectFilter, setSubjectFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("upcoming");
  useLucideIcons([students, dialogOpen, isMobile]);

  // Derive last-lesson status per student from events
  const lastEventStatus = useMemo(() => {
    const today = toDateStr(new Date());
    const map = new Map<number, { past: ScheduleEvent | null; future: ScheduleEvent | null }>();
    for (const ev of events) {
      if (!ev.date) continue;
      const ids = [...new Set([ev.student_id, ...(ev.studentIds ?? [])].filter((x): x is number => x != null))];
      for (const sid of ids) {
        if (!map.has(sid)) map.set(sid, { past: null, future: null });
        const cur = map.get(sid)!;
        if (ev.date <= today) {
          if (!cur.past || ev.date > cur.past.date!) cur.past = ev;
        } else {
          if (!cur.future || ev.date < cur.future.date!) cur.future = ev;
        }
      }
    }
    const result = new Map<number, string>();
    for (const [sid, { past, future }] of map) {
      result.set(sid, (past ?? future)!.status);
    }
    return result;
  }, [events]);

  useEffect(() => {
    api.students().then(setStudents).catch(() => setStudents([]));
  }, []);

  // External trigger: toolbar "新增学员" button
  useEffect(() => {
    if (addOpen) {
      setEditStudent(undefined);
      setDialogOpen(true);
    }
  }, [addOpen]);

  const closeDialog = () => {
    setDialogOpen(false);
    onAddClose?.();
  };

  const openEdit = (s: Student) => {
    setEditStudent(s);
    setDialogOpen(true);
  };

  const handleSaved = (saved: Student) => {
    setStudents(prev =>
      prev.some(s => s.id === saved.id)
        ? prev.map(s => (s.id === saved.id ? saved : s))
        : [...prev, saved]
    );
    onStudentSaved?.(saved);
    closeDialog();
  };

  const handleDeleted = (id: number) => {
    setStudents(prev => prev.filter(s => s.id !== id));
    onStudentDeleted?.(id);
    closeDialog();
  };

  // Derive unique filter options from actual students data
  const allGrades = [...new Set(students.map(s => s.grade))].sort();
  const allSubjects = [...new Set(students.map(s => s.subject))].sort();

  const filtered = students.filter(s => {
    if (searchTerm.trim() && !s.name.includes(searchTerm) && !s.grade.includes(searchTerm) && !s.subject.includes(searchTerm)) return false;
    if (gradeFilter && s.grade !== gradeFilter) return false;
    if (subjectFilter && s.subject !== subjectFilter) return false;
    if (typeFilter && s.type !== typeFilter) return false;
    if (statusFilter && s.status !== statusFilter) return false;
    return true;
  });

  const hasFilter = gradeFilter || subjectFilter || typeFilter || statusFilter;

  const th = {
    textAlign: "left" as const,
    fontSize: 12,
    fontWeight: 700,
    color: "var(--text-subtle)",
    padding: "12px 14px",
    whiteSpace: "nowrap" as const,
  };
  const td = {
    padding: "12px 14px",
    borderTop: "1px solid var(--divider)",
    fontSize: 14,
    color: "var(--text-body)",
    verticalAlign: "middle" as const,
    whiteSpace: "nowrap" as const,
  };

  return (
    <>
      {/* Filter bar */}
      <div style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap", marginBottom: 12 }}>
        <div style={{ minWidth: 120 }}>
          <Select
            label="年级"
            value={gradeFilter}
            placeholder="全部年级"
            options={allGrades.map(g => ({ value: g, label: g }))}
            onChange={e => setGradeFilter(e.target.value)}
          />
        </div>
        <div style={{ minWidth: 120 }}>
          <Select
            label="科目"
            value={subjectFilter}
            placeholder="全部科目"
            options={allSubjects.map(s => ({ value: s, label: s }))}
            onChange={e => setSubjectFilter(e.target.value)}
          />
        </div>
        <div style={{ minWidth: 120 }}>
          <Select
            label="课型"
            value={typeFilter}
            placeholder="全部课型"
            options={TYPE_OPTIONS}
            onChange={e => setTypeFilter(e.target.value)}
          />
        </div>
        <div style={{ minWidth: 120 }}>
          <Select
            label="状态"
            value={statusFilter}
            placeholder="全部状态"
            options={[
              { value: "upcoming", label: "在读" },
              { value: "taught", label: "已毕业" },
              { value: "leave", label: "请假中" },
              { value: "absent", label: "暂停" },
            ]}
            onChange={e => setStatusFilter(e.target.value)}
          />
        </div>
        {hasFilter && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => { setGradeFilter(""); setSubjectFilter(""); setTypeFilter(""); setStatusFilter(""); }}
            style={{ marginBottom: 1, color: "var(--text-subtle)" }}
          >
            清除筛选
          </Button>
        )}
        <div style={{ marginLeft: "auto", fontSize: 13, color: "var(--text-subtle)", alignSelf: "center" }}>
          共 {filtered.length} 位学员
        </div>
      </div>

      {/* ── Mobile card list ── */}
      {isMobile ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {filtered.length === 0 && (
            <div style={{ textAlign: "center", color: "var(--text-subtle)", padding: "40px 0", fontSize: 14 }}>
              {searchTerm ? "未找到匹配学员" : "暂无学员，点击右上角「新增学员」添加"}
            </div>
          )}
          {filtered.map((s) => (
            <div
              key={s.id}
              style={{
                background: "var(--surface-card)", border: "1px solid var(--divider)",
                borderRadius: "var(--radius-md)", padding: "14px 14px 12px",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                <Avatar name={s.name} size="sm" />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, color: "var(--text-strong)", fontSize: 15 }}>{s.name}</div>
                  <div style={{ fontSize: 12, color: "var(--text-subtle)" }}>{s.phone || "—"}</div>
                </div>
                <StatusPill status={(lastEventStatus.get(s.id) ?? s.status) as "taught" | "upcoming" | "leave" | "absent"} />
              </div>
              <div style={{ display: "flex", gap: 5, marginBottom: 10, flexWrap: "wrap" }}>
                <Tag tone="brand">{s.grade}</Tag>
                <Tag>{s.subject}</Tag>
                <Tag>{s.type}</Tag>
              </div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ fontSize: 13, color: "var(--text-subtle)" }}>
                  剩余{" "}
                  <span style={{
                    fontWeight: 800, fontFamily: "var(--font-num)",
                    color: s.remain <= 1 ? "var(--danger-fg)" : "var(--text-strong)",
                  }}>
                    {s.remain.toFixed(1)}
                  </span>{" "}课时
                </div>
                <div style={{ display: "flex", gap: 6 }}>
                  <IconButton label="课程日历" size="sm" onClick={() => setCalendarStudent(s)}>
                    <Icon name="calendar-days" size={17} />
                  </IconButton>
                  <IconButton label="排课" size="sm" tone="brand" onClick={() => onSchedule?.(s)}>
                    <Icon name="calendar-plus" size={17} />
                  </IconButton>
                  <IconButton label="编辑" size="sm" onClick={() => openEdit(s)}>
                    <Icon name="pencil" size={17} />
                  </IconButton>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
      /* ── Desktop table ── */
      <Card pad={false} style={{ overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead style={{ background: "var(--surface-sunken)" }}>
            <tr>
              <th style={{ ...th, width: 36, paddingRight: 0 }}>
                <Checkbox />
              </th>
              <th style={th}>学员</th>
              <th style={th}>年级 · 科目</th>
              <th style={th}>课型</th>
              <th style={{ ...th, textAlign: "right" }}>剩余课时</th>
              <th style={th}>状态</th>
              <th style={{ ...th, textAlign: "right" }}>操作</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr>
                <td colSpan={7} style={{ ...td, textAlign: "center", color: "var(--text-subtle)", padding: "32px 14px" }}>
                  {searchTerm ? "未找到匹配学员" : "暂无学员，点击右上角「新增学员」添加"}
                </td>
              </tr>
            )}
            {filtered.map((s) => (
              <tr key={s.id} style={{ transition: "background 0.1s" }}>
                <td style={{ ...td, paddingRight: 0 }}>
                  <Checkbox />
                </td>
                <td style={td}>
                  <div style={{ display: "flex", alignItems: "center", gap: 11 }}>
                    <Avatar name={s.name} size="sm" />
                    <div>
                      <div style={{ fontWeight: 700, color: "var(--text-strong)" }}>{s.name}</div>
                      <div style={{ fontSize: 12, color: "var(--text-subtle)", fontVariantNumeric: "tabular-nums" }}>
                        {s.phone || "—"}
                      </div>
                    </div>
                  </div>
                </td>
                <td style={td}>
                  <div style={{ display: "flex", gap: 6 }}>
                    <Tag tone="brand">{s.grade}</Tag>
                    <Tag>{s.subject}</Tag>
                  </div>
                </td>
                <td style={td}>{s.type}</td>
                <td style={{ ...td, textAlign: "right" }}>
                  <span style={{ fontFamily: "var(--font-num)", fontWeight: 800, fontVariantNumeric: "tabular-nums", color: s.remain <= 1 ? "var(--danger-fg)" : "var(--text-strong)" }}>
                    {s.remain.toFixed(1)}
                  </span>
                  <span style={{ fontSize: 12, color: "var(--text-subtle)" }}> 课时</span>
                </td>
                <td style={td}>
                  <StatusPill status={(lastEventStatus.get(s.id) ?? s.status) as "taught" | "upcoming" | "leave" | "absent"} />
                </td>
                <td style={{ ...td, textAlign: "right" }}>
                  <div style={{ display: "inline-flex", gap: 2 }}>
                    <IconButton label="课程日历" size="sm" onClick={() => setCalendarStudent(s)}>
                      <Icon name="calendar-days" size={17} />
                    </IconButton>
                    <IconButton label="排课" size="sm" tone="brand" onClick={() => onSchedule?.(s)}>
                      <Icon name="calendar-plus" size={17} />
                    </IconButton>
                    <IconButton label="编辑" size="sm" onClick={() => openEdit(s)}>
                      <Icon name="pencil" size={17} />
                    </IconButton>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
      )}

      <StudentDialog
        open={dialogOpen}
        editStudent={editStudent}
        onClose={closeDialog}
        onSaved={handleSaved}
        onDeleted={handleDeleted}
      />

      <StudentCalendarDialog
        open={!!calendarStudent}
        student={calendarStudent}
        events={events}
        onClose={() => setCalendarStudent(undefined)}
      />
    </>
  );
}
